import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Share2, Heart, MessageCircle } from "lucide-react";
import { getProduct, getProducts, getImageUrl } from "@/lib/api";
import { useStore } from "@/lib/store";
import { Badge } from "@/components/ui/badge";
import ProductCard from "@/components/ProductCard";
import SizzhkaLoader from "@/components/SizzhkaLoader";
import { toast } from "sonner";

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState("");
  const [imgIdx, setImgIdx] = useState(0);
  const { addToCart, formatPrice, t, setCartOpen, toggleFavorite, isFavorite, telegramContact } = useStore();

  useEffect(() => {
    setLoading(true);
    setImgIdx(0);
    setSelectedSize("");
    getProduct(id).then((p) => {
      setProduct(p);
      if (p.sizes?.length) setSelectedSize(p.sizes[0]);
      return getProducts({ category: p.category, limit: 4 });
    }).then((data) => {
      setRelated((data.products || []).filter((p) => p.id !== id).slice(0, 4));
    }).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  if (loading || !product) {
    return <SizzhkaLoader />;
  }

  const images = product.images?.length ? product.images : [];
  const hasDiscount = product.old_price && product.old_price > product.price;
  const statusMap = { "in-stock": t("inStock"), "pre-order": t("preOrder"), "sold-out": t("soldOut") };
  const fav = isFavorite(product.id);

  const handleAddToCart = () => {
    if (!selectedSize) {
      toast.error(t("selectSize"));
      return;
    }
    if (product.status === "sold-out") {
      toast.error(t("soldOut"));
      return;
    }
    addToCart(product, selectedSize, 1);
    toast.success(t("addedToCart"));
  };

  const handleBuyNow = () => {
    if (!product) return;
    if (product.sizes?.length && !selectedSize) {
      toast.error(t("selectSize"));
      return;
    }
    if (product.status === "sold-out") {
      toast.error(t("soldOut"));
      return;
    }
    addToCart(product, selectedSize, 1);
    // Open cart drawer or navigate to cart
    if (setCartOpen) {
      setCartOpen(true);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: product.title, url });
      } else if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        toast.success(t("linkCopied"));
      } else {
        window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(product.title)}`, "_blank");
      }
    } catch {
      window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(product.title)}`, "_blank");
    }
  };

  const handleToggleFavorite = () => {
    toggleFavorite(product);
    if (!fav) {
      toast.success(t("addedToFavorites"));
    } else {
      toast.success(t("removedFromFavorites"));
    }
  };

  const handleAskQuestion = () => {
    const contact = telegramContact || "XruuXruu";
    window.open(`https://t.me/${contact}`, "_blank");
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="animate-fade-in" data-testid="product-page">
      {/* Back */}
      <div className="px-4 py-2 border-b border-zinc-800">
        <button onClick={handleGoBack} className="inline-flex items-center gap-1 text-xs font-mono text-zinc-500 hover:text-white transition-colors" data-testid="back-link">
          <ChevronLeft className="w-4 h-4" /> {t("catalog")}
        </button>
      </div>

      {/* Desktop: 2 columns / Mobile: stacked */}
      <div className="md:grid md:grid-cols-2">
        {/* Image Gallery */}
        <div className="relative bg-zinc-950 overflow-hidden" data-testid="product-gallery">
          {/* Main image — 4:5 aspect, image fits inside without distortion */}
          <div className="aspect-[4/5] overflow-hidden relative bg-zinc-950">
            {images.length > 0 ? (
              <img src={getImageUrl(images[imgIdx], 1200)} alt={product.title}
                className="absolute inset-0 w-full h-full object-contain transition-opacity duration-300" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-zinc-700 font-mono text-sm">{t("noImage")}</div>
            )}
            {images.length > 1 && (
              <>
                <button onClick={() => setImgIdx((i) => (i > 0 ? i - 1 : images.length - 1))}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors z-10"
                  data-testid="gallery-prev">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={() => setImgIdx((i) => (i < images.length - 1 ? i + 1 : 0))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors z-10"
                  data-testid="gallery-next">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
            {/* Image counter */}
            {images.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
                {images.map((_, i) => (
                  <button key={i} onClick={() => setImgIdx(i)}
                    className={`w-1.5 h-1.5 transition-all ${i === imgIdx ? "bg-white scale-125" : "bg-zinc-600"}`} />
                ))}
              </div>
            )}
          </div>

          {/* Thumbnail strip — properly sized, below image */}
          {images.length > 1 && (
            <div className="flex gap-2 p-3 bg-black border-t border-zinc-800/50 overflow-x-auto">
              {images.map((img, i) => (
                <button key={i} onClick={() => setImgIdx(i)}
                  className={`w-16 h-20 border shrink-0 overflow-hidden transition-all bg-zinc-950 ${i === imgIdx ? "border-white ring-1 ring-white" : "border-zinc-700 opacity-50 hover:opacity-100"}`}>
                  <img src={getImageUrl(img, 160)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="px-4 md:px-8 py-6 space-y-4" data-testid="product-info">
          <div>
            {product.condition && (
              <Badge variant="outline" className="mb-2 rounded-none border-zinc-700 text-[10px] font-mono">{product.condition}</Badge>
            )}
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{product.title}</h1>
            <div className="flex items-center gap-3 mt-2">
              {hasDiscount && (
                <span className="text-sm font-mono text-zinc-500 line-through-red">{formatPrice(product.old_price)}</span>
              )}
              <span className="text-xl font-mono font-bold">{formatPrice(product.price)}</span>
            </div>
          </div>

          {/* Description */}
          {product.description && (
            <div>
              <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-1">{t("description")}</p>
              <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line">{product.description}</p>
            </div>
          )}

          {/* Condition & Status */}
          <div className="flex gap-8">
            {product.condition && (
              <div>
                <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-1">{t("condition")}</p>
                <p className="text-sm font-mono">{product.condition}</p>
              </div>
            )}
            <div>
              <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-1">{t("status")}</p>
              <p className="text-sm font-mono">{statusMap[product.status] || product.status}</p>
            </div>
          </div>

          {/* Size Selector */}
          {product.sizes?.length > 0 && (
            <div>
              <p className="text-xs font-mono text-zinc-500 uppercase tracking-wider mb-2">{t("size")}</p>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((size) => (
                  <button key={size} onClick={() => setSelectedSize(size)}
                    className={`min-w-[40px] h-10 px-3 text-xs font-mono border transition-colors ${
                      selectedSize === size
                        ? "bg-white text-black border-white"
                        : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                    }`}
                    data-testid={`size-${size}`}>
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2 pt-2">
            <button onClick={handleAddToCart}
              disabled={product.status === "sold-out"}
              className="w-full h-12 bg-white text-black font-display text-sm font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="add-to-cart-btn">
              {product.status === "sold-out" ? t("soldOut") : t("addToCart")}
            </button>
            <div className="flex gap-2">
              <button onClick={handleBuyNow}
                disabled={product.status === "sold-out"}
                className="flex-1 h-12 border border-white text-white font-display text-sm font-bold uppercase tracking-wider hover:bg-white hover:text-black transition-colors disabled:opacity-30"
                data-testid="buy-now-btn">
                {t("buy")}
              </button>
              <button onClick={handleToggleFavorite}
                className={`w-12 h-12 border flex items-center justify-center transition-colors ${
                  fav ? "border-white bg-white text-black" : "border-zinc-700 hover:bg-zinc-900"
                }`}
                data-testid="favorite-btn">
                <Heart className={`w-4 h-4 ${fav ? "fill-black" : ""}`} />
              </button>
              <button onClick={handleShare}
                className="w-12 h-12 border border-zinc-700 flex items-center justify-center hover:bg-zinc-900 transition-colors"
                data-testid="share-btn">
                <Share2 className="w-4 h-4" />
              </button>
            </div>
            {/* Ask Question button */}
            <button onClick={handleAskQuestion}
              className="w-full h-12 border border-zinc-700 text-zinc-300 font-display text-sm font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white transition-colors flex items-center justify-center gap-2"
              data-testid="ask-question-btn">
              <MessageCircle className="w-4 h-4" />
              {t("askQuestion")}
            </button>
          </div>
        </div>
      </div>

      {/* Related Products */}
      {related.length > 0 && (
        <section className="border-t border-zinc-800 py-6 px-4" data-testid="related-section">
          <h2 className="font-display text-base tracking-wider mb-4">{t("youMayLike")}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 border border-zinc-800">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}